/*
 * Carbisdale - ProseEditor
 *
 * Facade-Klasse die Model, View und Input zusammenführt.
 * Stellt die öffentliche API für den Editor bereit.
 */

import { EditorModel, Position } from './EditorModel.js'
import { EditorView } from './EditorView.js'
import { EditorInput } from './EditorInput.js'
import { EditorCleanup } from './EditorCleanup.js'
import { OutlineView } from './OutlineView.js'
import { ContextView } from './ContextView.js'
import { getWordBounds } from '../TextProcessor.js'
import { TIMING, EDITOR, LINE_HEIGHT_Q } from '../config.js'

/**
 * ProseEditor-Klasse
 * Haupteinstiegspunkt für den Prose-Editor.
 */
export class ProseEditor {
    /**
     * Erstellt einen neuen ProseEditor.
     * @param {Object} bounds - {x, y, width, height} des Editor-Bereichs
     */
    constructor(bounds) {
        // Datenmodell
        this.model = new EditorModel()

        // View für Rendering
        this.view = new EditorView(
            this.model,
            bounds.width,
            bounds.height
        )

        // Input-Handler
        this.input = new EditorInput(this.model, this.view)

        // Rueckverweis fuer EditorModel.jumpToSection (Phase 4 Sub-Phase 4.2):
        // Die Auto-Apply-Engine ruft modelMethod auf EditorModel; jumpToSection
        // delegiert hierueber an EditorInput._navigateToLink.
        this.model._editorInput = this.input

        // Outline-View für den Outline-Pane
        this.outlineView = new OutlineView(this.model, this.view)

        // Context-View für den Context-Pane
        this.contextView = new ContextView(this.model, this.view)

        // Cleanup-Callback setzen
        this.input.onLeaveParagraph = (paragraphId) => {
            EditorCleanup.cleanupParagraph(this.model, paragraphId)
        }

        // Separator-Input-State-Accessor: View kann den SingleLineInput-State abfragen
        this.view.setSeparatorInputAccessor(() => this.input.getSeparatorInputState())

        // Callback für blockierte Aktionen (wird von main.js gesetzt)
        this._onActionBlocked = null

        // Bounds speichern für spätere Updates
        this.bounds = { ...bounds }

        // Für Note-Klick-Zählung (Double/Triple-Click)
        this._noteClickCount = 0
        this._lastNoteClickTime = 0
        this._lastNoteClickPos = null

        // Für Info-Klick-Zählung (Double/Triple-Click)
        this._infoClickCount = 0
        this._lastInfoClickTime = 0
        this._lastInfoClickPos = null

        // Für Marker-Doppelklick (Outline-Subtree-Selection)
        this._lastMarkerClickTime = 0
        this._lastMarkerClickParaId = null

    }

    /**
     * Fuehrt einen Remote-Apply-Block aus: setzt das Remote-Flag am Model,
     * ruft fn() (wo typischerweise setParagraphsRT o.ae. passiert), baut
     * die View neu auf und setzt Model + View auf clean. Der Flag sorgt
     * dafuer dass persistence.saveContent() den Batch nicht als lokale
     * Aenderung missinterpretiert und zurueck zum Server pusht.
     *
     * @param {() => void} fn
     */
    applyRemoteChanges(fn) {
        this.model.beginApplyRemote()
        try {
            fn()
            this.view.rebuildIfDirty()
            this.model.markClean()
        } finally {
            this.model.endApplyRemote()
        }
    }

    /**
     * Setzt den Callback für blockierte Aktionen (Ping-Alarm).
     * @param {Function} fn - Wird aufgerufen wenn eine Aktion blockiert ist
     */
    setActionBlockedCallback(fn) {
        this.input.onActionBlocked = fn
    }

    // ===== Event-Handling (delegiert an Input) =====

    /**
     * Behandelt ein Keydown-Event.
     * @param {KeyboardEvent} e
     * @returns {boolean} true wenn Event behandelt wurde
     */
    handleKeydown(e) {
        return this.input.handleKeydown(e)
    }

    /**
     * Behandelt Mouse-Down (Drag-Initialisierung).
     * @param {number} x - X relativ zur Pane
     * @param {number} y - Y relativ zur Pane
     * @param {boolean} shiftKey
     */
    handleMouseDown(x, y, shiftKey, fractionalY, pixelX) {
        this.input.handleMouseDown(x, y, shiftKey, fractionalY, pixelX)
    }

    /**
     * Prüft ob eine Screen-Y-Position auf einer Separator-Zeile liegt.
     * @param {number} localY - Y relativ zur Pane
     * @returns {boolean}
     */
    isSeparatorAtY(localY) {
        const lineIndex = this.view.screenYToLineIndex(localY)
        return lineIndex >= 0 && lineIndex < this.view.lines.length
            && this.view.lines[lineIndex].isSeparator
    }

    /**
     * Behandelt einen Mausklick (nach mouseup).
     * @param {number} x - X relativ zur Pane
     * @param {number} y - Y relativ zur Pane
     * @param {boolean} shiftKey
     * @param {number} [pixelX] - Rohe Pixel-X-Position (proportional)
     */
    handleClick(x, y, shiftKey, pixelX, opts) {
        this.input.handleClick(x, y, shiftKey, pixelX, opts)
    }

    /**
     * Behandelt Maus-Bewegung (für Drag-Selection).
     * @param {number} x
     * @param {number} y
     * @param {number} [pixelX] - Rohe Pixel-X-Position (proportional)
     */
    handleMouseMove(x, y, fractionalY, pixelX) {
        this.input.handleMouseMove(x, y, fractionalY, pixelX)
    }

    /**
     * Behandelt Mouse-Up.
     * @param {number} x
     * @param {number} y
     */
    handleMouseUp(x, y) {
        this.input.handleMouseUp(x, y)
    }

    /**
     * Behandelt Mouse-Wheel (Scrollen).
     * @param {number} deltaY - Scroll-Betrag
     * @param {number} deltaMode - 0=Pixel, 1=Lines, 2=Pages
     */
    handleMouseWheel(deltaY, deltaMode) {
        this.input.handleMouseWheel(deltaY, deltaMode)
    }

    // ===== Clipboard (delegiert an Input) =====

    /**
     * Kopiert den selektierten Text.
     * @returns {string}
     */
    handleCopy() {
        return this.input.handleCopy()
    }

    /**
     * Schneidet den selektierten Text aus.
     * @returns {string}
     */
    handleCut() {
        const text = this.input.handleCut()
        this.view.markDirty()
        return text
    }

    /**
     * Fügt Text ein.
     * @param {string} text
     */
    handlePaste(text, html) {
        const result = this.input.handlePaste(text, html)
        this.view.markDirty()
        return result
    }

    /**
     * Setzt die Höhe des Scrollbar-Pane (wenn Scrollbar in anderem Pane als Main).
     * @param {number|null} height - Höhe oder null für Standard (Editor-Höhe)
     */
    set scrollbarViewHeight(height) {
        this.input.scrollbarViewHeight = height
    }

    /**
     * Prüft ob eine X-Position auf der Scrollbar liegt.
     * @param {number} x - X relativ zur Pane
     * @returns {boolean}
     */
    isOnScrollbar(x) {
        return this.view.isOnScrollbar(x)
    }

    /**
     * Ob gerade ein Scrollbar-Drag aktiv ist.
     * @returns {boolean}
     */
    get isScrollbarDragging() {
        return this.input.isScrollbarDragging
    }

    /**
     * Touch-Optimierung Phase 5: liefert die Pane-lokalen Screen-Positionen
     * von Anchor und Cursor der aktuellen Selection. Wird von mouseHandlers
     * gebraucht, um zu entscheiden, ob ein Touch im Slop um einen der
     * Selection-Endpunkte liegt (Handle-Drag-Erkennung).
     *
     * Annahme: Anchor und Cursor liegen im selben Feld — Cross-Field-Selections
     * gibt es heute nicht (verschiedene Felder = verschiedene Render-Pfade).
     *
     * @returns {{anchor:{x:number,y:number,heightQ:number,pixelX?:number},
     *            cursor:{x:number,y:number,heightQ:number,pixelX?:number},
     *            field:'text'|'note'|'info'}|null}
     */
    getSelectionScreenPositions() {
        const sel = this.model.selection
        if (!sel || sel.isCollapsed()) return null

        const field = sel.anchor.field || 'text'
        let posFn
        if (field === 'text') posFn = (p, o) => this.view.dataToScreen(p, o)
        else if (field === 'note') posFn = (p, o) => this.view.noteDataToScreen(p, o)
        else if (field === 'info') posFn = (p, o) => this.view.infoDataToScreen(p, o)
        else return null

        const anchorPos = posFn(sel.anchor.paragraphId, sel.anchor.offset)
        const cursorPos = posFn(sel.cursor.paragraphId, sel.cursor.offset)
        if (!anchorPos || !cursorPos) return null

        return { anchor: anchorPos, cursor: cursorPos, field }
    }

    // ===== Rendering (delegiert an View) =====

    /**
     * Prüft ob der Editor neu gerendert werden muss.
     * @returns {boolean}
     */
    needsRender() {
        return this.view.isDirty || this.model.isDirty
    }

    /**
     * Rendert den Editor-Inhalt (nur Text, kein Cursor).
     * Der Cursor wird vom Framework-Renderer gezeichnet.
     *
     * @param {number} offsetX - Absoluter X-Offset
     * @param {number} offsetY - Absoluter Y-Offset
     * @param {Function} setCharFn - Funktion (x, y, char, style)
     */
    render(offsetX, offsetY, setCharFn) {
        this.view.render(offsetX, offsetY, setCharFn)
        this.model.markClean()
        this.view.markClean()
    }

    /**
     * Versucht nur die Zeilen dirty-markierter Paragraphs zu rendern (Paragraph-Partial).
     * Bei Erfolg werden model und view clean markiert.
     *
     * @param {number} offsetX - Pane X-Offset
     * @param {number} offsetY - Pane Y-Offset
     * @param {Function} setCharFn - Render-Callback
     * @param {Function} clearLineFn - Funktion (charX, charY, charWidth) zum Löschen einer Zeile
     * @returns {Array|null} Array von dirty Ranges bei Erfolg, null bei nötigem Full-Render
     */
    tryRenderPartial(offsetX, offsetY, setCharFn, clearLineFn) {
        const dirtyRanges = this.view.tryRenderDirtyOnly(offsetX, offsetY, setCharFn, clearLineFn)
        if (dirtyRanges) {
            this.model.markClean()
            this.view.markClean()
        }
        return dirtyRanges
    }

    /**
     * Spell-Repaint (V3.1): zeichnet nur die Absaetze neu, fuer die frische
     * Spellcheck-Warnings vorliegen. Beruehrt den model-/view-Dirty-State
     * nicht — es hat sich kein Content geaendert.
     *
     * @returns {Array|null} dirty Ranges oder null wenn nichts zu tun war
     */
    trySpellRepaint(offsetX, offsetY, setCharFn, clearLineFn) {
        return this.view.trySpellRepaint(offsetX, offsetY, setCharFn, clearLineFn)
    }

    /**
     * Rendert die Scrollbar an einer externen Position.
     * Wird von main.js aufgerufen, um die Scrollbar im zuständigen Pane zu zeichnen.
     *
     * @param {number} x - Absolute X-Position
     * @param {number} y - Absolute Y-Position
     * @param {number} height - Höhe der Scrollbar
     * @param {Function} setCharFn - Funktion (x, y, char, style)
     */
    renderScrollbar(x, y, height, setCharFn) {
        this.view.renderScrollbar(x, y, height, setCharFn)
    }

    /**
     * Gibt die Cursor-Position relativ zur Pane zurück.
     * Wird vom Framework verwendet um Pane.cursorPosition zu aktualisieren.
     *
     * @returns {{x: number, y: number}|null}
     */
    getPaneCursorPosition() {
        return this.view.getCursorPosition()
    }

    /**
     * Aktualisiert die Editor-Dimensionen.
     * @param {Object} bounds - {x, y, width, height}
     * @param {number} noteWidth - Breite für Note-Text im Outline (0 = Outline nicht sichtbar)
     */
    resize(bounds, noteWidth = 0, infoWidth = 0) {
        this.bounds = { ...bounds }
        this.view.resize(bounds.width, bounds.height, noteWidth, infoWidth)
    }

    /**
     * Wechselt das Cursor-Feld (text ↔ note).
     * @param {string} newField - 'text' oder 'note'
     */
    switchField(newField) {
        this.input.switchField(newField)
    }

    /**
     * Wechselt zum Note-Feld mit temporärer Aktivierung für passive Absätze.
     * Normal-Paras werden temporär als Outline-Item aktiviert (depth+1).
     * Die Aktivierung wird rückgängig gemacht wenn keine Note eingegeben wird.
     */
    switchToNote() {
        if (!this.model.cursor) return

        const para = this.model.getParagraph(this.model.cursor.paragraphId)
        if (!para) return

        // Passiver Absatz → temporär aktivieren
        if (!para.isOutlineItem()) {
            this.input._activateTemporarily(para)
        }

        this.input.switchField('note')
    }

    /**
     * Gibt die Note-Cursor-Position relativ zum Outline-Pane zurück.
     * Die X-Position beinhaltet den Offset für Outline-Spalten und > Marker.
     *
     * @returns {{x: number, y: number}|null}
     */
    getNotePaneCursorPosition() {
        if (!this.model.cursor || this.model.cursor.field !== 'note') return null
        if (this.view.outlinePaneWidth <= 0) return null

        const notePos = this.view.noteDataToScreen(
            this.model.cursor.paragraphId,
            this.model.cursor.offset
        )
        if (!notePos) return null

        // X-Offset: Note-Start-Column + 1 (> Marker) + noteX
        const noteStartCol = this.view.getNoteStartCol(this.model.cursor.paragraphId)
        const result = {
            x: noteStartCol + 1 + notePos.x,
            y: notePos.y,
            heightQ: notePos.heightQ,
            pixelYOffset: notePos.pixelYOffset || 0
        }

        // Proportional: Pixel-Position für I-Beam-Cursor weiterleiten
        if (notePos.pixelX !== undefined && this.view.renderer) {
            const r = this.view.renderer
            // > Marker in 4QL (System-Font) → markerWidthPx(SECONDARY) für korrekte Position
            const markerPx = r.isProportional() ? r.markerWidthPx(LINE_HEIGHT_Q.SECONDARY) : r.charWidth
            result.pixelX = noteStartCol * r.charWidth + markerPx + notePos.pixelX
        }

        return result
    }

    /**
     * Behandelt einen Klick im Outline-Pane auf den Note-Bereich.
     * Wechselt zum Note-Feld und setzt den Cursor.
     *
     * @param {number} localX - X relativ zum Outline-Pane innerBounds
     * @param {number} localY - Y relativ zum Outline-Pane innerBounds
     */
    /**
     * Behandelt einen Separator-Control-Klick.
     * @param {{ type: string, paragraphId: number }} control
     */
    _handleSeparatorControl(control) {
        const para = this.model.getParagraph(control.paragraphId)
        if (!para || para.type !== 'separator') return

        switch (control.type) {
            case 'depth_decrease':
                if (para.outlineDepth > 0) {
                    this.model._actionBlocked = false
                    this.model.setSeparatorDepth(control.paragraphId, para.outlineDepth - 1)
                    if (this.model._actionBlocked && this.input._pingBlocked) {
                        this.input._pingBlocked()
                    }
                }
                break
            case 'depth_increase':
                if (para.outlineDepth < 6) {
                    this.model._actionBlocked = false
                    this.model.setSeparatorDepth(control.paragraphId, para.outlineDepth + 1)
                    if (this.model._actionBlocked && this.input._pingBlocked) {
                        this.input._pingBlocked()
                    }
                }
                break
            case 'insert_separator': {
                const idx = this.model.getParagraphIndex(control.paragraphId)
                if (idx >= 0) {
                    this.model.insertSeparatorBefore(idx, para.outlineDepth)
                }
                break
            }
            case 'collapse':
                // Cursor auf das Separator-Label setzen, damit der Cmd+Enter-Pfad
                // (mit Feld-Sprung etc.) konsistent greift.
                if (!this.model.cursor || this.model.cursor.paragraphId !== control.paragraphId) {
                    this.model.cursor = new Position(control.paragraphId, 0, 'text')
                }
                this.input._handleCollapseToggle()
                break
            case 'lock':
                this.model.toggleSectionLock(control.paragraphId)
                this.view.markDirty()
                break
        }
    }

    /**
     * Gemeinsame Logik fuer Note/Info-Positionsberechnung aus Pane-Koordinaten.
     * @param {string} field - 'note' oder 'info'
     * @param {number} localX - X relativ zur Pane
     * @param {number} localY - Y relativ zur Pane
     * @param {number|null} pixelX - Pixel-X fuer proportional
     * @returns {{dataPos: {paragraphId, offset}, para: Object}|null}
     */
    _resolveFieldPosition(field, localX, localY, pixelX) {
        const lines = field === 'note' ? this.view.noteLines : this.view.infoLines
        const lineIndex = this.view.screenYToLineIndex(localY, field)
        if (lineIndex < 0 || lineIndex >= lines.length) return null

        // Padding-Zeilen (vor Background-Separator) ignorieren
        if (lines[lineIndex].isPadding) return null

        // Startkolumne: Note hat dynamischen Einzug, Info hat festen > Marker
        const startCol = field === 'note'
            ? this.view.getNoteStartCol(lines[lineIndex].paragraphId) + 1
            : 1
        const textX = localX - startCol

        if (textX < 0) return null

        // Proportional: Pixel-Position relativ zum Textbereich
        let fieldPixelX = null
        if (pixelX !== null && pixelX !== undefined && this.view.renderer && this.view.renderer.isProportional()) {
            const r = this.view.renderer
            // startCol enthält noteStartCol + 1 (oder 1 für Info),
            // die letzte Spalte (+1) ist der > Marker → 4QL-System-Font-Breite nutzen
            const markerOffset = (startCol - 1) * r.charWidth + r.markerWidthPx(LINE_HEIGHT_Q.SECONDARY)
            fieldPixelX = pixelX - markerOffset
        }

        const screenToData = field === 'note'
            ? this.view.noteScreenToData.bind(this.view)
            : this.view.infoScreenToData.bind(this.view)
        const dataPos = screenToData(textX, localY, fieldPixelX)
        if (!dataPos) return null

        const para = this.model.getParagraph(dataPos.paragraphId)
        if (!para) return null

        return { dataPos, para }
    }

    _resolveNotePosition(localX, localY, pixelX) {
        return this._resolveFieldPosition('note', localX, localY, pixelX)
    }

    /**
     * Selektiert den ganzen Zuordnungsbereich (Outline-Subtree) eines
     * Outline-Items als Outline-Selection. Geteilt von Marker-Doppelklick im
     * Outline-Pane und dem Context-Menu "Select".
     * @param {string} paragraphId - Outline-Item
     */
    selectOutlineSubtree(paragraphId) {
        const paraIndex = this.model.getParagraphIndex(paragraphId)
        const range = this.model.getSubtreeRange(paraIndex)
        this.model.setOutlineSelection(range.startIndex, range.endIndex)
        this.view.ensureCursorVisible()
    }

    /**
     * Context-Menu "Select": selektiert je nach Cursor-Feld den ganzen Absatz
     * (text/Main) bzw. den ganzen Zuordnungsbereich (note/Outline) — identisch
     * zum Doppelklick auf den >-Absatzmarker.
     * @returns {'activateMain'|undefined} 'activateMain' nach Outline-Subtree-
     *   Selektion (Aufrufer aktiviert dann den Main-Pane, wie beim Doppelklick).
     */
    selectCurrentParagraph() {
        const cursor = this.model.cursor
        if (!cursor) return
        const para = this.model.getParagraph(cursor.paragraphId)
        if (!para) return
        const field = cursor.field || 'text'
        if (field === 'note' && para.isOutlineItem()) {
            this.selectOutlineSubtree(para.id)
            return 'activateMain'
        }
        if (field === 'text' && para.type !== 'separator') {
            this.input.selectWholeParagraph(para.id)
            this.view.ensureCursorVisible()
        }
    }

    handleNoteClick(localX, localY, pixelX) {
        // (-)/(+) Klick auf Active-Absatz: Collapse togglen
        if (this.outlineView) {
            const activeId = this.outlineView.getActiveCollapseIconParagraphId(localX, localY)
            if (activeId !== null) {
                if (!this.model.cursor || this.model.cursor.paragraphId !== activeId) {
                    this.model.cursor = new Position(activeId, 0, 'text')
                }
                this.input._handleCollapseToggle()
                return
            }
        }

        // Separator-Zeile: Controls oder Titel-Bearbeitung
        const lineIndex = this.view.screenYToLineIndex(localY)
        if (lineIndex >= 0 && lineIndex < this.view.lines.length && this.view.lines[lineIndex].isSeparator) {
            if (this.outlineView) {
                // Zuerst Controls prüfen
                const sepControl = this.outlineView.getSeparatorControlAtPosition(localX, localY)
                if (sepControl) {
                    this._handleSeparatorControl(sepControl)
                    return
                }
                // Kein Control getroffen → Titel-Bearbeitung nur wenn Main nicht sichtbar
                // (Titel wird nur einmal angezeigt, Priorität: Main > Outline > Context)
                if (this.view.mainPaneWidth > 0) return
                const sepLine = this.view.lines[lineIndex]
                // Background-Separator hat keinen editierbaren Titel
                if (sepLine.separatorDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
                    if (this.input.onPing) this.input.onPing()
                    return
                }
                this.model.cursor = new Position(sepLine.paragraphId, 0)
                this.model._markCursorDirty()
                const charOffset = this.outlineView.getSeparatorTitleCharOffset(localX, sepLine, pixelX)
                this.input._activateSeparatorInput(charOffset, 'outline')
            }
            return
        }

        // Doppelklick auf ">" Marker → Outline-Subtree-Selection
        if (lineIndex >= 0 && lineIndex < this.view.noteLines.length) {
            const noteLine = this.view.noteLines[lineIndex]
            if (noteLine && !noteLine.isPadding && !noteLine.isSeparator && noteLine.isFirstLine) {
                const markerPara = this.model.getParagraph(noteLine.paragraphId)
                if (markerPara && markerPara.isOutlineItem()) {
                    const noteStartCol = this.view.getNoteStartCol(markerPara.id)
                    if (localX === noteStartCol) {
                        // Klick auf ">" Marker
                        const now = Date.now()
                        if (this._lastMarkerClickParaId === markerPara.id &&
                            now - this._lastMarkerClickTime < TIMING.doubleClickThreshold) {
                            // Doppelklick → Subtree selektieren
                            this.selectOutlineSubtree(markerPara.id)
                            this._lastMarkerClickParaId = null
                            return 'activateMain'
                        }
                        this._lastMarkerClickTime = now
                        this._lastMarkerClickParaId = markerPara.id
                        return  // Single-Click auf ">": nichts tun
                    }
                }
            }
        }

        let resolved = this._resolveNotePosition(localX, localY, pixelX)

        // Klick links vom Note-Bereich: passiven Absatz trotzdem aktivieren
        if (!resolved) {
            const lineIndex = this.view.screenYToLineIndex(localY, 'note')
            if (lineIndex >= 0 && lineIndex < this.view.noteLines.length) {
                const noteLine = this.view.noteLines[lineIndex]
                if (noteLine && !noteLine.isPadding && !noteLine.isSeparator) {
                    const para = this.model.getParagraph(noteLine.paragraphId)
                    if (para && !para.isOutlineItem()) {
                        this.input._checkTempActivationRevert()
                        this.input._activateTemporarily(para)
                        this.model.cursor = new Position(para.id, 0, 'note')
                        this.model._markStructuralDirty()
                        this.input.switchField('note')
                        return
                    }
                }
            }
            return
        }
        const { dataPos, para } = resolved

        // Vorherige temporäre Aktivierung prüfen/reverten bevor neue startet
        this.input._checkTempActivationRevert()

        // Passiver Absatz → temporär aktivieren
        if (!para.isOutlineItem()) {
            this.input._activateTemporarily(para)
        }

        // Klick-Zählung für Double/Triple-Click
        const now = Date.now()
        const samePosition = this._lastNoteClickPos &&
            this._lastNoteClickPos.paragraphId === dataPos.paragraphId &&
            Math.abs(this._lastNoteClickPos.offset - dataPos.offset) <= 1

        if (samePosition && now - this._lastNoteClickTime < TIMING.doubleClickThreshold) {
            this._noteClickCount++
        } else {
            this._noteClickCount = 1
        }
        this._lastNoteClickTime = now
        this._lastNoteClickPos = dataPos

        if (this._noteClickCount === 3) {
            // Triple-Click: ganze Note selektieren
            this.model.setSelection(
                new Position(dataPos.paragraphId, 0, 'note'),
                new Position(dataPos.paragraphId, para.note.length, 'note')
            )
            this._noteClickCount = 0
            this.view.ensureCursorVisible()
            return
        }

        if (this._noteClickCount === 2) {
            // Collapsed Active: Subtree-Selection (wie > Marker im Outline)
            if (para.isOutlineItem() && para.activeCollapsed) {
                const idx = this.model.getParagraphIndex(para.id)
                const range = this.model.getSubtreeRange(idx)
                this.model.setOutlineSelection(range.startIndex, range.endIndex)
                this._noteClickCount = 0
                this.view.ensureCursorVisible()
                return
            }
            // Double-Click: Wort selektieren
            const wordBounds = getWordBounds(para.note, dataPos.offset)
            if (wordBounds) {
                this.model.setSelection(
                    new Position(dataPos.paragraphId, wordBounds.start, 'note'),
                    new Position(dataPos.paragraphId, wordBounds.end, 'note')
                )
            }
            this.view.ensureCursorVisible()
            return
        }

        // Single-Click
        if (this.input.justFinishedNoteDrag) {
            // Nach Drag-Selection: Selection beibehalten
            this.input.justFinishedNoteDrag = false
        } else {
            // Link-Klick: direkt aus screenToData-Ergebnis erkennen
            if (dataPos.isLink) {
                const sepMap = this.view._separatorMap
                const isBroken = !sepMap || !sepMap.has(dataPos.linkTargetId)
                if (!isBroken) {
                    this.input._navigateToLink(dataPos.linkTargetId)
                    return
                }
                this.input._pingBlocked()
                return
            }

            // URL-Klick: Web-Link im neuen Tab öffnen
            if (dataPos.isUrl) {
                this.input._openExternalUrl(dataPos.url)
                return
            }
            this.model.clearSelection()
        }
        this.model.setCursor(dataPos.paragraphId, dataPos.offset, 'note')
        this.input._resetGoalColumn()
        this.view.ensureCursorVisible()
    }

    /**
     * Startet eine Note-Drag-Selection bei Mousedown.
     * @param {number} localX - X relativ zur Outline-Pane
     * @param {number} localY - Y relativ zur Outline-Pane
     * @param {boolean} shiftKey
     */
    handleNoteMouseDown(localX, localY, shiftKey, pixelX) {
        const resolved = this._resolveNotePosition(localX, localY, pixelX)
        if (!resolved) return
        const { dataPos } = resolved

        this.input._noteDragStartPos = dataPos
        this.input._isNoteDragging = true
        this.input._hadNoteDragMovement = false

        if (shiftKey && this.model.cursor) {
            if (!this.model.selection) {
                this.model.startSelection()
            }
        } else {
            this.model.clearSelection()
        }
    }

    /**
     * Behandelt Mausbewegung für Note-Drag-Selection.
     * @param {number} localX - X relativ zur Outline-Pane
     * @param {number} localY - Y relativ zur Outline-Pane
     */
    handleNoteMouseMove(localX, localY, pixelX) {
        if (!this.input._isNoteDragging || !this.input._noteDragStartPos) return

        // Auto-Scroll bei Drag über Viewport-Grenzen
        if (localY < 0) {
            const distance = Math.abs(localY)
            this.input._startAutoScroll(-1, distance, localX, pixelX)
            this.input._hadNoteDragMovement = true
            return
        } else if (localY >= this.view.height) {
            const distance = localY - this.view.height + 1
            this.input._startAutoScroll(1, distance, localX, pixelX)
            this.input._hadNoteDragMovement = true
            return
        } else {
            this.input._stopAutoScroll()
        }

        const resolved = this._resolveNotePosition(localX, localY, pixelX)
        if (!resolved) return
        const { dataPos } = resolved

        // Nur innerhalb des gleichen Paragraphs (Notes haben keine Cross-Paragraph-Selection)
        if (dataPos.paragraphId !== this.input._noteDragStartPos.paragraphId) return

        this.input._hadNoteDragMovement = true

        const start = this.input._noteDragStartPos
        if (!this.model.selection) {
            this.model.setSelection(
                new Position(start.paragraphId, start.offset, 'note'),
                new Position(dataPos.paragraphId, dataPos.offset, 'note')
            )
        } else {
            this.model.setCursor(dataPos.paragraphId, dataPos.offset, 'note')
            this.model.extendSelection()
        }
    }

    /**
     * Beendet eine Note-Drag-Selection bei Mouseup.
     */
    handleNoteMouseUp() {
        this.input._stopAutoScroll()
        // Nur bei nicht-collapsed Selection als "Drag fertig" zählen — sonst
        // schluckt Mikro-Mausbewegung den nachfolgenden Link-Klick.
        const sel = this.model.selection
        if (this.input._isNoteDragging && this.input._hadNoteDragMovement &&
            sel && !sel.isCollapsed()) {
            this.input.justFinishedNoteDrag = true
        }
        this.input._isNoteDragging = false
        this.input._noteDragStartPos = null
    }

    // ===== Context-Pane (Info-Feld) =====

    /**
     * Wechselt zum Info-Feld (keine temporäre Aktivierung nötig).
     */
    switchToInfo() {
        this.input.switchField('info')
    }

    /**
     * Gibt die Info-Cursor-Position relativ zum Context-Pane zurück.
     * @returns {{x: number, y: number, pixelX?: number}|null}
     */
    getInfoPaneCursorPosition() {
        if (!this.model.cursor || this.model.cursor.field !== 'info') return null
        if (this.view.contextPaneWidth <= 0) return null

        const infoPos = this.view.infoDataToScreen(
            this.model.cursor.paragraphId,
            this.model.cursor.offset
        )
        if (!infoPos) return null

        // X-Offset: 1 (> Marker) + infoX
        const result = {
            x: 1 + infoPos.x,
            y: infoPos.y,
            heightQ: infoPos.heightQ,
            pixelYOffset: infoPos.pixelYOffset || 0
        }

        // Proportional: Pixel-Position für I-Beam-Cursor
        if (infoPos.pixelX !== undefined && this.view.renderer) {
            const r = this.view.renderer
            const markerPx = r.isProportional() ? r.markerWidthPx(LINE_HEIGHT_Q.SECONDARY) : r.charWidth
            result.pixelX = markerPx + infoPos.pixelX
        }

        return result
    }

    _resolveInfoPosition(localX, localY, pixelX) {
        return this._resolveFieldPosition('info', localX, localY, pixelX)
    }

    /**
     * Behandelt einen Klick im Context-Pane auf den Info-Bereich.
     */
    handleInfoClick(localX, localY, pixelX) {
        // Separator-Zeile: Klick im Context-Pane
        const lineIndex = this.view.screenYToLineIndex(localY, 'info')
        if (lineIndex >= 0 && lineIndex < this.view.lines.length && this.view.lines[lineIndex].isSeparator) {
            const sepLine = this.view.lines[lineIndex]
            if (sepLine.separatorDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) return

            // Lock-Control: erscheint im Context-Separator wenn Main hidden
            // (Position width - 4 .. width - 2, identisch zu Main).
            if (this.view.mainPaneWidth <= 0) {
                const ctxWidth = this.view.contextPaneWidth
                if (ctxWidth >= 8) {
                    const lockX = ctxWidth - 4
                    if (localX >= lockX && localX <= lockX + 2) {
                        this.model.toggleSectionLock(sepLine.paragraphId)
                        this.view.markDirty()
                        return
                    }
                }
            }

            // Titel-Bearbeitung nur wenn Context das einzig sichtbare Pane
            if (this.view.mainPaneWidth > 0 || this.view.outlinePaneWidth > 0) return
            this.model.cursor = new Position(sepLine.paragraphId, 0)
            this.model._markCursorDirty()
            // Context-Pane hat gleiches Layout wie Main: '── Titel ──'
            const charOffset = this.input._separatorClickCharOffset(localX, pixelX)
            this.input._activateSeparatorInput(charOffset, 'context')
            return
        }

        const resolved = this._resolveInfoPosition(localX, localY, pixelX)
        if (!resolved) return
        const { dataPos, para } = resolved

        // Klick-Zählung für Double/Triple-Click
        const now = Date.now()
        const samePosition = this._lastInfoClickPos &&
            this._lastInfoClickPos.paragraphId === dataPos.paragraphId &&
            Math.abs(this._lastInfoClickPos.offset - dataPos.offset) <= 1

        if (samePosition && now - this._lastInfoClickTime < TIMING.doubleClickThreshold) {
            this._infoClickCount++
        } else {
            this._infoClickCount = 1
        }
        this._lastInfoClickTime = now
        this._lastInfoClickPos = dataPos

        if (this._infoClickCount === 3) {
            // Triple-Click: ganzes Info selektieren
            this.model.setSelection(
                new Position(dataPos.paragraphId, 0, 'info'),
                new Position(dataPos.paragraphId, para.info.length, 'info')
            )
            this._infoClickCount = 0
            this.view.ensureCursorVisible()
            return
        }

        if (this._infoClickCount === 2) {
            // Double-Click: Wort selektieren
            const wordBounds = getWordBounds(para.info, dataPos.offset)
            if (wordBounds) {
                this.model.setSelection(
                    new Position(dataPos.paragraphId, wordBounds.start, 'info'),
                    new Position(dataPos.paragraphId, wordBounds.end, 'info')
                )
            }
            this.view.ensureCursorVisible()
            return
        }

        // Single-Click
        if (this.input.justFinishedInfoDrag) {
            this.input.justFinishedInfoDrag = false
        } else {
            // Link-Klick: direkt aus screenToData-Ergebnis erkennen
            if (dataPos.isLink) {
                const sepMap = this.view._separatorMap
                const isBroken = !sepMap || !sepMap.has(dataPos.linkTargetId)
                if (!isBroken) {
                    this.input._navigateToLink(dataPos.linkTargetId)
                    return
                }
                this.input._pingBlocked()
                return
            }

            // URL-Klick: Web-Link im neuen Tab öffnen
            if (dataPos.isUrl) {
                this.input._openExternalUrl(dataPos.url)
                return
            }
            this.model.clearSelection()
        }
        this.model.setCursor(dataPos.paragraphId, dataPos.offset, 'info')
        this.input._resetGoalColumn()
        this.view.ensureCursorVisible()
    }

    /**
     * Startet eine Info-Drag-Selection bei Mousedown.
     */
    handleInfoMouseDown(localX, localY, shiftKey, pixelX) {
        // Outline-Selection aufheben bei Context-Pane-Klick
        this.model.clearOutlineSelection()

        const resolved = this._resolveInfoPosition(localX, localY, pixelX)
        if (!resolved) return
        const { dataPos } = resolved

        this.input._infoDragStartPos = dataPos
        this.input._isInfoDragging = true
        this.input._hadInfoDragMovement = false

        if (shiftKey && this.model.cursor) {
            if (!this.model.selection) {
                this.model.startSelection()
            }
        } else {
            this.model.clearSelection()
        }
    }

    /**
     * Behandelt Mausbewegung für Info-Drag-Selection.
     */
    handleInfoMouseMove(localX, localY, pixelX) {
        if (!this.input._isInfoDragging || !this.input._infoDragStartPos) return

        // Auto-Scroll bei Drag über Viewport-Grenzen
        if (localY < 0) {
            const distance = Math.abs(localY)
            this.input._startAutoScroll(-1, distance, localX, pixelX)
            this.input._hadInfoDragMovement = true
            return
        } else if (localY >= this.view.height) {
            const distance = localY - this.view.height + 1
            this.input._startAutoScroll(1, distance, localX, pixelX)
            this.input._hadInfoDragMovement = true
            return
        } else {
            this.input._stopAutoScroll()
        }

        const resolved = this._resolveInfoPosition(localX, localY, pixelX)
        if (!resolved) return
        const { dataPos } = resolved

        // Nur innerhalb des gleichen Paragraphs
        if (dataPos.paragraphId !== this.input._infoDragStartPos.paragraphId) return

        this.input._hadInfoDragMovement = true

        const start = this.input._infoDragStartPos
        if (!this.model.selection) {
            this.model.setSelection(
                new Position(start.paragraphId, start.offset, 'info'),
                new Position(dataPos.paragraphId, dataPos.offset, 'info')
            )
        } else {
            this.model.setCursor(dataPos.paragraphId, dataPos.offset, 'info')
            this.model.extendSelection()
        }
    }

    /**
     * Beendet eine Info-Drag-Selection bei Mouseup.
     */
    handleInfoMouseUp() {
        this.input._stopAutoScroll()
        // Nur bei nicht-collapsed Selection als "Drag fertig" zählen — sonst
        // schluckt Mikro-Mausbewegung den nachfolgenden Link-Klick.
        const sel = this.model.selection
        if (this.input._isInfoDragging && this.input._hadInfoDragMovement &&
            sel && !sel.isCollapsed()) {
            this.input.justFinishedInfoDrag = true
        }
        this.input._isInfoDragging = false
        this.input._infoDragStartPos = null
    }

    // ===== Context Rendering =====

    /**
     * Rendert den Context-Bereich.
     */
    renderContext(offsetX, offsetY, width, height, setCharFn) {
        this.contextView.render(offsetX, offsetY, width, height, setCharFn)
    }

    /**
     * Rendert nur die neuen Context-Zeilen nach einem Scroll-Shift.
     */
    renderContextPartial(offsetX, offsetY, width, height, scrollDelta, setCharFn, shiftPixels = 0) {
        this.contextView.renderPartial(offsetX, offsetY, width, height, scrollDelta, setCharFn, shiftPixels)
    }

    /**
     * Rendert nur die dirty Context-Zeilen (Paragraph-Partial-Pfad).
     */
    renderContextDirtyRanges(offsetX, offsetY, width, height, dirtyRanges, setCharFn, clearLineFn) {
        this.contextView.renderDirtyRanges(offsetX, offsetY, width, height, dirtyRanges, setCharFn, clearLineFn)
    }

    // ===== Outline Rendering =====

    /**
     * Rendert den Outline-Bereich.
     *
     * @param {number} offsetX - X-Offset des Outline-Pane
     * @param {number} offsetY - Y-Offset
     * @param {number} width - Verfügbare Breite
     * @param {number} height - Verfügbare Höhe
     * @param {Function} setCharFn - Funktion (x, y, char, style)
     */
    renderOutline(offsetX, offsetY, width, height, setCharFn) {
        this.outlineView.render(offsetX, offsetY, width, height, setCharFn)
    }

    /**
     * Rendert nur die neuen Outline-Zeilen nach einem Scroll-Shift.
     */
    renderOutlinePartial(offsetX, offsetY, width, height, scrollDelta, setCharFn, shiftPixels = 0) {
        this.outlineView.renderPartial(offsetX, offsetY, width, height, scrollDelta, setCharFn, shiftPixels)
    }

    /**
     * Rendert nur die dirty Outline-Zeilen (Paragraph-Partial-Pfad).
     */
    renderOutlineDirtyRanges(offsetX, offsetY, width, height, dirtyRanges, setCharFn, clearLineFn) {
        this.outlineView.renderDirtyRanges(offsetX, offsetY, width, height, dirtyRanges, setCharFn, clearLineFn)
    }

    // ===== Link-Hover =====

    /**
     * Prüft ob eine Bildschirmposition über einem klickbaren Link ist.
     * Für Pointer-Cursor bei Mouse-Hover.
     * @param {'text'|'note'|'info'} pane - Welches Pane
     * @param {number} localX - Lokale X-Position im Pane
     * @param {number} localY - Lokale Y-Position im Pane
     * @param {number|null} pixelX - Pixel-X für proportionalen Modus
     * @returns {boolean}
     */
    isLinkAtPosition(pane, localX, localY, pixelX) {
        let pos
        if (pane === 'text') {
            pos = this.view.screenToData(localX, localY, pixelX)
        } else {
            const resolved = this._resolveFieldPosition(pane, localX, localY, pixelX)
            if (!resolved) return false
            pos = resolved.dataPos
        }
        if (!pos) return false
        return pos.isLink === true || pos.isUrl === true
    }

    // ===== Text-Zugriff (delegiert an Model) =====

    /**
     * Gibt den gesamten Text als Markdown zurück.
     * @returns {string}
     */
    getText() {
        return this.model.getText()
    }

    /**
     * Setzt den Text (Markdown).
     * @param {string} markdown
     */
    setText(markdown) {
        this.model.setText(markdown)
        this.view.viewportStart = 0
        this.view.markDirty()
    }

    // ===== Cursor-Position =====

    /**
     * Gibt die aktuelle Cursor-Position zurück.
     * @returns {{paragraphId: number, offset: number}|null}
     */
    getCursorPosition() {
        if (!this.model.cursor) return null
        return {
            paragraphId: this.model.cursor.paragraphId,
            offset: this.model.cursor.offset,
            field: this.model.cursor.field || 'text'
        }
    }

    /**
     * Gibt die Screen-Position des Cursors zurück.
     * @returns {{x: number, y: number}|null}
     */
    getCursorScreenPosition() {
        if (!this.model.cursor) return null
        return this.view.dataToScreen(
            this.model.cursor.paragraphId,
            this.model.cursor.offset
        )
    }

    // ===== Status =====

    /**
     * Gibt Informationen über den aktuellen Zustand zurück.
     * @returns {Object}
     */
    getStatus() {
        return {
            paragraphCount: this.model.getParagraphCount(),
            lineCount: this.view.getTotalLineCount(),
            canUndo: this.model.canUndo(),
            canRedo: this.model.canRedo(),
            hasSelection: this.model.hasSelection()
        }
    }

}
